/**
 * UDP Onboarding Form Schema — single source of truth for the entire form.
 *
 * Mirrors dating-schema's onboardingFormSchema:
 * - All fields from all 12 steps combined into one schema
 * - All fields optional (form starts empty, fills incrementally)
 * - Provides OnboardingFormData type (inferred from Zod)
 *
 * @module udp-schema/onboarding-form
 */

import { z } from 'zod';
import { step01ProfileSchema } from './step-01.schema.js';
import { step02PoliticalSchema } from './step-02.schema.js';
import { step03ElectoralSchema } from './step-03.schema.js';
import { step04LeadershipSchema } from './step-04.schema.js';
import { step05PsychologicalSchema } from './step-05.schema.js';
import { step06EducationSchema } from './step-06.schema.js';
import { step07EmploymentSchema } from './step-07.schema.js';
import { step08NeurodiversitySchema } from './step-08.schema.js';
import { step09HealthSchema } from './step-09.schema.js';
import { step10WorldViewSchema } from './step-10.schema.js';
import { step11ResidenceSchema } from './step-11.schema.js';
import { step12HouseholdSchema } from './step-12.schema.js';

/**
 * Unified form schema — all fields from all 12 steps, all optional.
 *
 * Used by:
 * - Frontend: useForm<OnboardingFormData>({ resolver: zodResolver(udpOnboardingFormSchema) })
 * - Backend: Final validation at profile submission
 *
 * All fields are optional because the form fills incrementally (1 question at a time).
 */
export const udpOnboardingFormSchema = z
  .object({
    // Step 1 — Profile
    ...step01ProfileSchema.shape,
    // Step 2 — Political
    ...step02PoliticalSchema.shape,
    // Step 3 — Electoral
    ...step03ElectoralSchema.shape,
    // Step 4 — Leadership
    ...step04LeadershipSchema.shape,
    // Step 5 — Psychological
    ...step05PsychologicalSchema.shape,
    // Step 6 — Education
    ...step06EducationSchema.shape,
    // Step 7 — Employment
    ...step07EmploymentSchema.shape,
    // Step 8 — Neurodiversity
    ...step08NeurodiversitySchema.shape,
    // Step 9 — Health
    ...step09HealthSchema.shape,
    // Step 10 — World View
    ...step10WorldViewSchema.shape,
    // Step 11 — Residence
    ...step11ResidenceSchema.shape,
    // Step 12 — Household
    ...step12HouseholdSchema.shape,

    // UI-only fields (not in step schemas but used during onboarding)
    sameAsFamily: z.boolean().optional(),
    guardianPhone: z
      .object({
        relation: z.string().optional(),
        phone: z.string().optional(),
      })
      .optional(),
  })
  .partial();

/** Inferred form data type — used for useForm<OnboardingFormData>() */
export type OnboardingFormData = z.infer<typeof udpOnboardingFormSchema>;
